import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import Cookies from 'js-cookie';
import { Col, Collapse, Container, Row, Stack } from 'react-bootstrap';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import moment from 'moment';
import { ColorContext, UniqueWidgetRefreshContext } from 'App';
import BeaufortIcons from 'components/BeaufortIcons/BeaufortIcons';
import DropdownSelector from 'components/DropdownSelector';
import ForecastSpread from 'components/ForecastSpread';
import { useAPIData } from 'hooks/useAPIData';
import WidgetLoadingAnimation from './WidgetLoadingAnimation';
import WidgetTimeUpdate from './WidgetTimeUpdate';
import { ErrorMessage } from './ErrorMessage';
import { getDetailedMarine7 } from 'services/weather/marine.service';

const ROUTE = 'getDetailedMarine7';
const DAY_SEGMENTS = ['Morning', 'Afternoon', 'Evening', 'Night'];
const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const spreadStyle = { marginTop: 15, height: '100%', fontSize: 14 };

// Cache mirror of data so re-mounting the widget is instant.
let dataCache;

const buildCityIDs = (entries = []) => {
  const uniqueIds = new Set();
  const cityIDs = [];

  entries.forEach((entry) => {
    if (uniqueIds.has(entry.city_id)) return;

    const isNumericId = Boolean(parseInt(entry.city_id, 10));
    const displayName = isNumericId ? entry.city_name : `Near ${entry.city_name}`;

    cityIDs.push({ id: entry.city_id, display: displayName });
    uniqueIds.add(entry.city_id);
  });

  cityIDs.sort((item) => {
    if (parseInt(item.id, 10)) return -1;
    return 1;
  });

  return cityIDs;
};

// Spread builders stay identical to the larger marine forecast layout.
const buildWaveData = (entry) => ({
  'Wave Height': `${entry.wave_height} ft`,
  'Mean Wave Dir./Period': `${entry.mean_wave_direction_short} / ${entry.mean_wave_period} sec`,
  'Wind Wave Dir./Period': `${entry.wind_wave_direction_short} / ${entry.wind_wave_period} sec`,
  'Peak Wave Dir./Period': `${entry.peak_wave_direction_short} / ${entry.peak_wave_period} sec`,
});

const buildWindData = (entry) => ({
  'Wind Speed & Direction': `${entry.wind_speed} / ${entry.wind_short}`,
  'Wind Gusts': `${entry.wind_gusts}`,
  'Beaufort Description': `${entry.beaufort_desc}`,
  'Sea Surface Temp': `${entry.sea_surface_temp}°F`,
});

const formatEntryDate = (entry) => {
  if (!entry) return null;
  const source = entry.utc_time ?? entry.iso8601;
  if (!source) return null;
  return moment(source).format('MM/DD/YYYY');
};

export default function WaveAndWindConditionsWidget({
  widgetRefresh,
  setWidgetRefresh,
  dimensions,
}) {
  const STORAGE_KEY = 'waveAndWindDropdownState';
  const theme = useContext(ColorContext);
  const uniqueRefreshTokens = useContext(UniqueWidgetRefreshContext);
  const api = useAPIData();
  const apiRef = useRef(api);

  // Keep the latest API helpers in a ref to avoid re-triggering effects.
  useEffect(() => {
    apiRef.current = api;
  }, [api]);

  const loadInitialState = () => {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
      if (stored && typeof stored === 'object') {
        return {
          wave: Boolean(stored.wave),
          wind: Boolean(stored.wind),
        };
      }
    } catch (err) {
      console.warn('Unable to parse dropdown state', err);
    }
    return { wave: true, wind: true }; // default when nothing saved
  };

  const initialState = loadInitialState();
  const [fetching, setFetching] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [data, setData] = useState([]);
  const [cityIDs, setCityIDs] = useState([]);
  const [selectedDay, setSelectedDay] = useState(new Date().getDay());
  const [selectedCityId, setSelectedCityId] = useState(0);
  const [error, setError] = useState({ state: false, message: '' });
  const [waveDropdown, setWaveDropdown] = useState(initialState.wave);
  const [windDropdown, setWindDropdown] = useState(initialState.wind);

  const locationKey = `${Cookies.get('city')}-${Cookies.get('state')}`;
  const uniqueKey = `${locationKey}-${ROUTE}`;
  const widgetRefreshToken = uniqueRefreshTokens[uniqueKey];

  const selectedWeekday = weekdays[selectedDay] ?? weekdays[0];
  const selectedCity = cityIDs[selectedCityId]?.id;

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ wave: waveDropdown, wind: windDropdown }));
  }, [waveDropdown, windDropdown]);

  // Keep the per-segment lookups memoized so we only iterate once per selection change.
  const filteredEntries = useMemo(() => {
    if (!selectedCity) return [];
    return data.filter(
      (entry) => entry.weekday === selectedWeekday && entry.city_id === selectedCity,
    );
  }, [data, selectedWeekday, selectedCity]);

  const segmentMap = useMemo(
    () =>
      DAY_SEGMENTS.reduce((acc, segment) => {
        acc[segment] = filteredEntries.find((entry) => entry.day_segment === segment) ?? null;
        return acc;
      }, {}),
    [filteredEntries],
  );

  const currentDateLabel = useMemo(() => formatEntryDate(filteredEntries[0]), [filteredEntries]);

  const waveNoDataCopy = useMemo(
    () => `No wave forecast data available for ${selectedWeekday}.`,
    [selectedWeekday],
  );

  const windNoDataCopy = useMemo(
    () => `No wind forecast data available for ${selectedWeekday}.`,
    [selectedWeekday],
  );

  const waveSpreads = useMemo(
    () =>
      DAY_SEGMENTS.map((segment) => {
        const entry = segmentMap[segment];
        if (!entry) return null;
        return (
          <ForecastSpread
            key={`wave-${segment}`}
            header={
              <div className="d-flex align-items-center justify-content-between w-100 fw-bold fs-6">
                <div>{segment}</div>
                <div className="text-capitalize">{entry.beaufort_desc}</div>
              </div>
            }
            data={buildWaveData(entry)}
            icon={<BeaufortIcons conditionId={entry.beaufort} />}
            style={spreadStyle}
          />
        );
      }),
    [segmentMap],
  );

  const windSpreads = useMemo(
    () =>
      DAY_SEGMENTS.map((segment) => {
        const entry = segmentMap[segment];
        if (!entry) return null;
        return (
          <ForecastSpread
            key={`wind-${segment}`}
            header={
              <div className="d-flex align-items-center justify-content-between w-100 fw-bold fs-6">
                <div>{segment}</div>
                <div className="text-capitalize">{entry.beaufort_desc}</div>
              </div>
            }
            data={buildWindData(entry)}
            icon={<BeaufortIcons conditionId={entry.beaufort} />}
            style={spreadStyle}
          />
        );
      }),
    [segmentMap],
  );

  useEffect(() => {
    if (!cityIDs.length) {
      if (selectedCityId !== 0) {
        setSelectedCityId(0);
      }
      return;
    }

    if (selectedCityId >= cityIDs.length) {
      setSelectedCityId(0);
    }
  }, [cityIDs, selectedCityId]);

  useEffect(() => {
    if (!dataCache) return;
    setData(dataCache);
    setCityIDs(buildCityIDs(dataCache));
    setFetching(false);
    setIsInitialLoad(false);
  }, []);

  const loadData = useCallback(async (force = false) => {
    const hasCache = Array.isArray(dataCache) && dataCache.length > 0;

    if (hasCache) {
      setIsRefreshing(true);
    } else {
      setIsInitialLoad(true);
    }

    setFetching(true);
    setError({ state: false, message: '' });

    try {
      const requester = force ? apiRef.current.forceGet : apiRef.current.get;
      const response = await requester({
        key: ROUTE,
        apiFunction: getDetailedMarine7,
      });
      const result = response?.data?.result ?? [];
      dataCache = result;
      setData(result);
      setCityIDs(buildCityIDs(result));
    } catch (err) {
      setError({
        state: true,
        message: err?.message ?? 'Error fetching data',
      });
    } finally {
      setFetching(false);
      setIsInitialLoad(false);
      setIsRefreshing(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Remote/toolbar refresh
  useEffect(() => {
    loadData(true);
  }, [loadData, widgetRefresh, widgetRefreshToken]);

  if (error.state && !fetching) {
    return (
      <ErrorMessage
        message={error.message}
        retryRoute={ROUTE}
        onRetry={() => {
          setError({ state: false, message: '' });
          setFetching(true);
        }}
      />
    );
  }

  if (isInitialLoad)
    return <WidgetLoadingAnimation widgetName={'WaveAndWindCondition'} dimensions={dimensions} />;

  const renderDropdowns = () => (
    <div className="d-flex flex-column flex-md-row gap-3 pb-3">
      <DropdownSelector
        options={weekdays}
        selectedOption={selectedDay}
        setSelected={(nextDay) => setSelectedDay(nextDay)}
        triggerStyle={{ width: 220, padding: 10 }}
        contentStyle={{ width: 220 }}
      />
      <DropdownSelector
        options={cityIDs.map((item) => item.display)}
        selectedOption={selectedCityId}
        setSelected={(nextCity) => setSelectedCityId(nextCity)}
        triggerStyle={{ width: 220, padding: 10 }}
        contentStyle={{ width: 220 }}
      />
    </div>
  );

  // Shared card presentation so we can swap between the desktop row and mobile stack.
  const renderCard = (title, spreads, emptyCopy, isCollapsible, isOpen, toggle) => {
    const cardContent = filteredEntries.length ? (
      <Stack gap={3} className="flex-grow-1">
        {spreads}
      </Stack>
    ) : (
      <div className="text-secondary small">{emptyCopy}</div>
    );

    if (!isCollapsible) {
      return (
        <div className="bg-base rounded-3 p-3 h-100 d-flex flex-column position-relative">
          <div className="border-bottom pb-2 mb-3">
            <div className="fs-5 fw-bold">{title}</div>
            <div className="small text-secondary">{currentDateLabel ?? 'No data'}</div>
            {isRefreshing && <div className="small text-secondary mt-1">Updating…</div>}
          </div>
          {cardContent}
        </div>
      );
    }

    return (
      <div
        className="bg-base rounded-3 p-3 h-100 d-flex flex-column position-relative"
        role="button"
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggle();
          }
        }}
      >
        <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
          <div className="fs-5 fw-bold">{title}</div>
          <div className="d-flex align-items-center gap-2 text-secondary small">
            {currentDateLabel ?? 'No data'}
            {isOpen ? <FaChevronUp /> : <FaChevronDown />}
          </div>
        </div>
        <Collapse in={isOpen}>
          <div className="pt-2">{cardContent}</div>
        </Collapse>
      </div>
    );
  };

  const isSingleColumn = dimensions[0] === 1;

  return (
    <Container fluid className="d-flex flex-column h-100 px-0 user-select-none text-white">
      <WidgetTimeUpdate route={ROUTE} dataUpdateOnly />

      <div className="flex-grow-1 overflow-auto px-3">
        {renderDropdowns()}

        {isSingleColumn ? (
          <Stack gap={3} className="pb-3">
            {renderCard('Wave', waveSpreads, waveNoDataCopy, true, waveDropdown, () =>
              setWaveDropdown((prev) => !prev),
            )}
            {renderCard('Wind', windSpreads, waveNoDataCopy, true, windDropdown, () =>
              setWindDropdown((prev) => !prev),
            )}
          </Stack>
        ) : (
          <Row className="g-4 pb-3 flex-column flex-lg-row flex-nowrap flex-lg-wrap">
            <Col xs={12} lg={6} className="d-flex">
              {renderCard('Wave', waveSpreads, waveNoDataCopy, false, false)}
            </Col>
            <Col xs={12} lg={6} className="d-flex">
              {renderCard('Wind', windSpreads, waveNoDataCopy, false, false)}
            </Col>
          </Row>
        )}
      </div>
    </Container>
  );
}

